import { getFileExtensionFromFileName, getFontAwesomeIconForFileExtension } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Popup } from 'semantic-ui-react';
import style from './file-display-card.less';
import { SignatureModal } from '../SignatureModal';
import { FilePreviewModal } from '../FilePreviewModal';

interface FileDisplayCardProps {
  fileTitle: string;
  fileTitleLink?: string;
  fileName?: string;
  azureBlobName?: string;
  showNoFileMessage?: boolean;
  checkIconTooltip?: string;
  previewIconTooltip?: string;
  downloadIconTooltip?: string;
  signature?: string;
  signatureIconTooltip?: string;
  hasMultipleFiles?: boolean;
  isDeleted?: boolean;
  fetchUrl?: () => Promise<string | undefined>;
  fetchBlob?: () => Promise<Blob>;
  handleDownload?: () => void;
  handlePreview?: () => void;
}

export const FileDisplayCard = observer(
  ({
    fileTitle,
    fileTitleLink,
    fileName,
    azureBlobName,
    showNoFileMessage,
    checkIconTooltip,
    previewIconTooltip,
    downloadIconTooltip,
    signature,
    signatureIconTooltip,
    hasMultipleFiles,
    isDeleted,
    fetchBlob,
    fetchUrl,
    handleDownload,
    handlePreview,
  }: FileDisplayCardProps) => {
    const [shouldShowSignatureModal, setShouldShowSignatureModal] = useState(false);
    const [shouldShowFilePreviewModal, setShouldShowFilePreviewModal] = useState(false);

    const checkIcon = (
      <FontAwesomeIcon
        className={style.file_display_card_check_icon}
        icon={['far', 'check-circle']}
        color="#56A86C"
      />
    );

    const getPreviewIcon = () => {
      if (!handlePreview) {
        return null;
      }

      const previewIcon = (
        <FontAwesomeIcon
          icon={['fas', 'eye']}
          color="#2483c8"
          style={{ marginRight: 16, cursor: 'pointer' }}
          onClick={() => {
            if (handlePreview) {
              handlePreview();
            } else {
              setShouldShowFilePreviewModal(true);
            }
          }}
        />
      );

      return previewIconTooltip ? (
        <Popup content={previewIconTooltip} trigger={previewIcon} />
      ) : (
        previewIcon
      );
    };

    const getDownloadIcon = () => {
      if (!handleDownload) {
        return null;
      }

      const downloadIcon = (
        <FontAwesomeIcon icon={['fas', 'download']} color="#D7AB73" onClick={handleDownload} />
      );

      return downloadIconTooltip ? (
        <Popup content={downloadIconTooltip} trigger={downloadIcon} />
      ) : (
        downloadIcon
      );
    };

    const fileTitleElement = (
      <div className={classNames(style.file_display_card_content_file_title)}>{fileTitle}</div>
    );

    const cardContent = (
      <div
        className={classNames(
          style.file_display_card,
          fileName ? undefined : style.no_file,
          isDeleted ? style.deleted : undefined,
        )}
      >
        <div className={style.file_display_card_content}>
          <div className={style.file_display_card_content_left}>
            <FontAwesomeIcon
              className={style.file_display_card_content_icon}
              icon={[
                'fal',
                fileName
                  ? getFontAwesomeIconForFileExtension(getFileExtensionFromFileName(fileName))
                  : 'file-minus',
              ]}
              color="#979797"
            />

            <div>
              {isDeleted ? (
                fileTitleElement
              ) : (
                <a href={fileTitleLink ?? '#'} target="_blank" rel="noreferrer">
                  {fileTitleElement}
                </a>
              )}

              {!fileName && showNoFileMessage && (
                <div
                  className={classNames(style.file_display_card_content_file_title, style.no_file)}
                >
                  No files uploaded by employee
                </div>
              )}
            </div>
          </div>

          <div className={style.icons_container}>
            {checkIconTooltip ? (
              <Popup content={checkIconTooltip} trigger={<span>{checkIcon}</span>} />
            ) : (
              checkIcon
            )}

            {signature ? (
              <Popup
                content={signatureIconTooltip ?? "View the employee's signature"}
                trigger={
                  <FontAwesomeIcon
                    icon={['far', 'file-signature']}
                    color="#4d7297"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setShouldShowSignatureModal(true);
                    }}
                  />
                }
              />
            ) : null}
          </div>

          {(fileName || hasMultipleFiles) && (
            <div className={style.file_display_card_download_icon_container}>
              {getPreviewIcon()}

              {getDownloadIcon()}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <>
        {isDeleted ? (
          <Popup content="Archived" position="top center" trigger={<div>{cardContent}</div>} />
        ) : (
          cardContent
        )}

        {signature && shouldShowSignatureModal && (
          <SignatureModal
            visible={shouldShowSignatureModal}
            signature={signature}
            onClose={() => setShouldShowSignatureModal(false)}
          />
        )}

        {fileName && azureBlobName && shouldShowFilePreviewModal && (
          <FilePreviewModal
            visible={shouldShowFilePreviewModal}
            fileName={fileName}
            azureBlobName={azureBlobName}
            fetchBlob={fetchBlob}
            fetchUrl={fetchUrl}
            onClose={() => setShouldShowFilePreviewModal(false)}
          />
        )}
      </>
    );
  },
);
